/**
 * GlobalPortalShellToggle — top-right view-mode pill on every page.
 *
 * Locked 2026-05-31. Two-state design (owner directive after live preview
 * showed the full pill overlapping the Veteran Owned badge on the Gateway):
 *
 *   COLLAPSED (default at rest):
 *     - Tiny 28px circular button showing ONLY the current mode's icon
 *     - 50% opacity + low-key glass background — doesn't compete visually
 *     - Same fixed top-right slot, so consistent location across every page
 *
 *   EXPANDED (after tap / hover):
 *     - Full Auto/Mobile/Desktop pill + Info popover button
 *     - Auto-collapses after 3.5s of no interaction
 *
 * Skips rendering when any OTHER `view-mode-toggle` is already on the
 * page (Provider Desktop chrome, RemodDashboard inline, SuperAdmin
 * header inline) so users always see exactly ONE toggle per page.
 *
 * Browser Desktop Site defense unchanged:
 *   - MutationObserver on <meta name="viewport"> re-applies the
 *     ViewModeContext value within 200ms if anything tampers
 *   - One-time sonner toast on UA-spoof detection
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { Info, Wand2, Smartphone, Monitor } from 'lucide-react';
import ViewModeToggle from './ViewModeToggle';
import { useViewMode } from '@/contexts/ViewModeContext';

const HINT_KEY = 'remod_browser_desktop_hint_shown';
const MOBILE_VIEWPORT = 'width=device-width, initial-scale=1, viewport-fit=cover';
const DESKTOP_VIEWPORT = 'width=1280, initial-scale=0.4, minimum-scale=0.25, maximum-scale=5, user-scalable=yes';
const AUTO_COLLAPSE_MS = 3500;

const GlobalPortalShellToggle = () => {
  const { effectiveMode, mode } = useViewMode();
  const location = useLocation();
  const expectedContentRef = useRef(
    effectiveMode === 'desktop' ? DESKTOP_VIEWPORT : MOBILE_VIEWPORT,
  );
  const [hideBecauseInlineToggle, setHideBecauseInlineToggle] = useState(false);
  const [topBannerPresent, setTopBannerPresent] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [guidanceOpen, setGuidanceOpen] = useState(false);
  const collapseTimerRef = useRef(null);
  const hintShownRef = useRef(false);

  // Slot policy (locked 2026-06-01 — Toggle-Only Left Corner Doctrine):
  //   • Top-LEFT corner on every page (right side has too much chrome).
  //   • On the Gateway (`/`), drop below the sticky logo header so the
  //     toggle sits visually under the logo instead of behind it.
  //   • On `/super-admin/*`, yield the top-left slot to GatewayHomeButton
  //     (the only page where the Gateway button is allowed) — toggle
  //     moves down so both stay on the left without colliding.
  //   • On Provider Desktop: nothing else lives in the top-left slot
  //     anymore (chrome back-button removed; each page uses its own
  //     per-page back arrow via StandardBackButton). Toggle owns top-left.
  // When the fixed impersonation banner (40px, z-9999) is on screen, drop
  // below it — otherwise the toggle is buried under the banner (founder bug
  // report 2026-09-26: "no toggle while on admin account").
  const slotClasses = useMemo(() => {
    const isGateway = location.pathname === '/';
    const isSuperAdmin = location.pathname.startsWith('/super-admin');
    if (isGateway) return topBannerPresent ? 'top-[124px] left-3' : 'top-[84px] left-3';
    if (isSuperAdmin) return topBannerPresent ? 'top-[92px] left-3' : 'top-[52px] left-3';
    return topBannerPresent ? 'top-[52px] left-3' : 'top-2 left-3';
  }, [location.pathname, topBannerPresent]);

  const popoverSlotClasses = useMemo(() => {
    const isGateway = location.pathname === '/';
    const isSuperAdmin = location.pathname.startsWith('/super-admin');
    if (isGateway) return topBannerPresent ? 'top-[164px] left-3' : 'top-[124px] left-3';
    if (isSuperAdmin) return topBannerPresent ? 'top-[132px] left-3' : 'top-[92px] left-3';
    return topBannerPresent ? 'top-[92px] left-3' : 'top-12 left-3';
  }, [location.pathname, topBannerPresent]);

  useEffect(() => {
    expectedContentRef.current = effectiveMode === 'desktop' ? DESKTOP_VIEWPORT : MOBILE_VIEWPORT;
  }, [effectiveMode]);

  // Hide when ANY inline `view-mode-toggle` (NOT our own inner one) is on
  // the page so users see exactly ONE toggle per page.
  useEffect(() => {
    const probe = () => {
      const all = document.querySelectorAll('[data-testid="view-mode-toggle"]');
      let otherFound = false;
      all.forEach((node) => {
        if (!node.closest('[data-testid="global-portal-viewmode-toggle"]')) {
          otherFound = true;
        }
      });
      setHideBecauseInlineToggle(otherFound);
      setTopBannerPresent(!!document.querySelector(
        '[data-testid="impersonation-banner"], [data-testid="owner-impersonation-banner"]',
      ));
    };
    probe();
    const iv = setInterval(probe, 300);
    return () => clearInterval(iv);
  }, []);

  // Viewport-meta tamper-guard — restore our intent + nudge user
  useEffect(() => {
    const tag = document.querySelector('meta[name="viewport"]');
    if (!tag) return undefined;
    let lastRestoredAt = 0;
    const observer = new MutationObserver((mutations) => {
      for (const m of mutations) {
        if (m.type !== 'attributes' || m.attributeName !== 'content') continue;
        const current = tag.getAttribute('content') || '';
        const expected = expectedContentRef.current;
        if (current === expected) continue;
        const now = Date.now();
        if (now - lastRestoredAt < 200) continue;
        lastRestoredAt = now;
        tag.setAttribute('content', expected);
        try {
          if (!hintShownRef.current && !sessionStorage.getItem(HINT_KEY)) {
            hintShownRef.current = true;
            sessionStorage.setItem(HINT_KEY, '1');
            toast.message(
              'For the best experience, switch between Mobile and Desktop inside RemodDash using the view toggle.',
              { duration: 6000, id: 'remod-browser-vm-hint' },
            );
          }
        } catch { /* ignore */ }
      }
    });
    observer.observe(tag, { attributes: true });
    return () => observer.disconnect();
  }, []);

  // UA-spoof heuristic — same as before, once-per-session
  useEffect(() => {
    let cancelled = false;
    const check = () => {
      try {
        if (cancelled || hintShownRef.current) return;
        if (sessionStorage.getItem(HINT_KEY)) return;
        const ua = navigator.userAgent || '';
        const claimsMobile = /Mobi|Android|iPhone|iPad|iPod/i.test(ua);
        const physicallySmall = (window.screen?.width || 9999) <= 480;
        if (physicallySmall && !claimsMobile && (window.innerWidth || 0) > 700) {
          hintShownRef.current = true;
          sessionStorage.setItem(HINT_KEY, '1');
          toast.message(
            'For the best experience, switch between Mobile and Desktop inside RemodDash using the view toggle.',
            { duration: 6000, id: 'remod-browser-vm-hint-ua' },
          );
        }
      } catch { /* ignore */ }
    };
    const t = setTimeout(check, 800);
    return () => { cancelled = true; clearTimeout(t); };
  }, []);

  // Auto-collapse expanded state after a beat of no interaction
  const scheduleCollapse = useCallback(() => {
    clearTimeout(collapseTimerRef.current);
    collapseTimerRef.current = setTimeout(() => {
      setExpanded(false);
      setGuidanceOpen(false);
    }, AUTO_COLLAPSE_MS);
  }, []);

  const handleExpand = useCallback(() => {
    setExpanded(true);
    scheduleCollapse();
  }, [scheduleCollapse]);

  const keepAlive = useCallback(() => {
    if (expanded) scheduleCollapse();
  }, [expanded, scheduleCollapse]);

  useEffect(() => () => clearTimeout(collapseTimerRef.current), []);

  if (hideBecauseInlineToggle) return null;

  // Collapsed (default at rest) — tiny, semi-transparent, current-mode glyph only.
  if (!expanded) {
    const Icon = mode === 'mobile' ? Smartphone : mode === 'desktop' ? Monitor : Wand2;
    return (
      <button
        type="button"
        onClick={handleExpand}
        data-testid="global-portal-viewmode-toggle"
        aria-label="Open view mode toggle"
        className={`fixed ${slotClasses} z-[5000] w-7 h-7 rounded-full bg-white/40 hover:bg-white/95 border border-white/50 backdrop-blur-sm flex items-center justify-center text-slate-600 hover:text-slate-900 shadow-sm transition-all duration-150 opacity-50 hover:opacity-100`}
        style={{ pointerEvents: 'auto' }}
      >
        <Icon className="w-3.5 h-3.5" />
      </button>
    );
  }

  // Expanded — full toggle + info popover, auto-collapses on idle.
  // Note: we deliberately do NOT reset the collapse timer on mouse-move —
  // cursor hovering nearby would otherwise hold the pill open indefinitely
  // and re-introduce the "always-prominent" problem. Only an explicit
  // click on a child element resets the timer.
  return (
    <div
      className={`fixed ${slotClasses} z-[5000] flex items-center gap-1.5`}
      data-testid="global-portal-viewmode-toggle"
      style={{ pointerEvents: 'auto' }}
      onClick={keepAlive}
    >
      <ViewModeToggle compact />

      <div className="relative">
        <button
          type="button"
          onClick={() => { setGuidanceOpen((v) => !v); keepAlive(); }}
          className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-white/90 border border-slate-200 text-slate-600 hover:text-slate-900 hover:bg-white shadow-sm transition-colors"
          aria-label="View mode help"
          aria-expanded={guidanceOpen}
          data-testid="view-mode-guidance-toggle"
        >
          <Info className="w-3.5 h-3.5" />
        </button>
      </div>

      {guidanceOpen && (
        <div
          className={`fixed ${popoverSlotClasses} w-72 p-3 rounded-2xl bg-white border border-slate-200 shadow-xl text-[12px] text-slate-700 leading-snug z-[5001]`}
          data-testid="view-mode-guidance-popover"
          role="tooltip"
        >
          <p className="font-semibold text-slate-900 mb-1">View Mode</p>
          <p>
            Use the RemodDash view toggle to switch between Mobile and Desktop —
            not your browser&apos;s &ldquo;Request Desktop Site.&rdquo; The in-app toggle is
            the single source of truth and prevents layout corruption.
          </p>
        </div>
      )}
    </div>
  );
};

export default GlobalPortalShellToggle;
